import React, { useState, useRef, useEffect } from 'react';
import { FiEye, FiEyeOff, FiMail, FiLock, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'sonner';
import axiosInstance from '../utils/axios-instance';

const ForgotPasswordPage = () => {
  // State management
  const [email, setEmail] = useState('');
  const [currentStep, setCurrentStep] = useState('email'); // email, otp, password, success
  const [formValues, setFormValues] = useState({
    otp: ['', '', '', ''],
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countdown, setCountdown] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  useEffect(() => {
    // Only start countdown when on the OTP step and countdown is positive
    if (currentStep === 'otp' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      // Enable the button when countdown reaches zero
      setIsResendDisabled(false);
    }
  }, [countdown, currentStep]);

  // Refs for OTP inputs
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  // Focus the first OTP input when OTP step is shown
  useEffect(() => {
    if (currentStep === 'otp' && otpRefs[0].current) {
      otpRefs[0].current.focus();
    }
  }, [currentStep]);

  // Validate email format
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Handle OTP input change and auto-focus
  const handleOtpChange = (index, value) => {
    // Allow only numbers
    if (value && !/^\d+$/.test(value)) return;

    // Update OTP state
    const newOtp = [...formValues.otp];
    newOtp[index] = value;
    setFormValues({ ...formValues, otp: newOtp });

    // Auto move to next input
    if (value && index < 3) {
      otpRefs[index + 1].current.focus();
    }
  };

  // Handle backspace in OTP fields
  const handleOtpKeyDown = (index, e) => {
    // Move to previous input on backspace if current field is empty
    if (e.key === 'Backspace' && !formValues.otp[index] && index > 0) {
      otpRefs[index - 1].current.focus();
    }
  };

  // Paste OTP from clipboard
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');

    // Check if pasted data is a 4-digit number
    if (/^\d{4}$/.test(pasteData)) {
      const digits = pasteData.split('');
      setFormValues({
        ...formValues,
        otp: digits
      });

      // Focus the last input
      otpRefs[3].current.focus();
    }
  };

  // Send OTP handler
  const handleSendOtp = async () => {
    // Clear previous errors
    setErrors({});

    // Validate email
    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }

    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    // Set loading state
    setLoading(true);

    try {
      // Send OTP via API
      var dataToSend = { email: email };
      const res = await axiosInstance.post('/user/generateotp', dataToSend);
      if (res.status === 200) {
        toast.success(res.data.message);
        setCurrentStep('otp');
        setErrors({});
        
        // Reset countdown when entering OTP step
        setCountdown(30);
        setIsResendDisabled(true);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle OTP resend
  const handleResendOtp = () => {
    // Call your original handleSendOtp function
    handleSendOtp();

    // Reset the countdown and disable the button again
    setCountdown(30);
    setIsResendDisabled(true);
  };

  // Verify OTP handler
  const handleVerifyOtp = async () => {
    // Clear previous errors
    setErrors({});

    // Validate OTP
    const otpValue = formValues.otp.join('');
    if (otpValue.length !== 4) {
      setErrors({ otp: 'Please enter the complete 4-digit OTP' });
      return;
    }

    // Set loading state
    setLoading(true);

    try {
      // Verify OTP via API
      var dataToSend = { email: email, otp: otpValue };
      const res = await axiosInstance.post('/user/verifyotp', dataToSend);
      if (res.status === 200) {
        toast.success(res.data.message);
        setCurrentStep('password');
        setErrors({});
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset password handler
  const handleResetPassword = async () => {
    // Clear previous errors
    setErrors({});

    // Validate passwords
    const newErrors = {};

    if (!formValues.password) {
      newErrors.password = 'New password is required';
    } else if (formValues.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formValues.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formValues.password !== formValues.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Set loading state
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const otpValue = formValues.otp.join('');
      var dataToSend = { email: email, otp: otpValue, password: formValues.password };
      const res = await axiosInstance.post('/user/resetpassword', dataToSend);
      if (res.status === 200) {
        toast.success(res.data.message);
        setCurrentStep('success');
        setErrors({});
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f1829]">
  <div className="w-full max-w-md mx-auto p-7 bg-[#162035] border border-white/10 rounded-2xl shadow-2xl">
    <h2 className="text-2xl font-bold text-center mb-6 text-white">Reset Password</h2>

    {currentStep === 'success' ? (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <FiCheckCircle className="text-green-500" size={50} />
        </div>
        <h3 className="text-xl font-semibold mb-2 dark:text-white">Password Reset Successful!</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">Your password has been updated successfully.</p>
        <button
          className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90"
          style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
          onClick={() => window.location.href = '/login'}
        >
          Login Now
        </button>
      </div>
    ) : (
      <div className="space-y-4">
        {/* Email Input - Step 1 */}
        {currentStep === 'email' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-white/40 mb-1.5 tracking-wide uppercase">Email Address</label>
              <div className="relative w-full">
                <input
                  className={`w-full px-4 py-2.5 pl-10 border rounded-xl text-white text-sm bg-[#1e2d4a] placeholder-white/20 focus:outline-none focus:ring-2 transition-all ${
                    errors.email ? 'border-red-500/60 focus:ring-red-500/30' : 'border-white/10 focus:ring-[#F05A22]/40 focus:border-[#F05A22]/50'
                  }`}
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30">
                  <FiMail size={18} />
                </span>
              </div>
              {errors.email && (
                <p className="text-red-400 text-xs mt-1.5 px-1">{errors.email}</p>
              )}
            </div>

            <button
              className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </>
        )}

        {/* OTP Input - Step 2 */}
        {currentStep === 'otp' && (
          <>
            <p className="text-white/50 text-sm text-center mb-2">
              We've sent a verification code to <span className="text-white font-medium">{email}</span>
            </p>

            <div>
              <label className="block text-xs font-semibold text-white/40 mb-1.5 tracking-wide uppercase">Enter 4-Digit OTP</label>
              <div className="flex justify-between gap-2 mb-1">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    ref={otpRefs[index]}
                    className={`w-full h-12 text-center text-lg font-bold border rounded-xl text-white bg-[#1e2d4a] focus:outline-none focus:ring-2 transition-all ${
                      errors.otp ? 'border-red-500/60 focus:ring-red-500/30' : 'border-white/10 focus:ring-[#F05A22]/40 focus:border-[#F05A22]/50'
                    }`}
                    type="text"
                    maxLength={1}
                    value={formValues.otp[index]}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                  />
                ))}
              </div>
              {errors.otp && (
                <p className="text-red-400 text-xs mt-1.5 px-1">{errors.otp}</p>
              )}

              <div className="mt-3 text-center">
                <button
                  className={`text-sm transition-colors ${
                    isResendDisabled ? 'text-white/20 cursor-not-allowed' : 'text-[#F05A22] hover:text-[#f47244]'
                  }`}
                  onClick={handleResendOtp}
                  disabled={loading || isResendDisabled}
                >
                  {isResendDisabled ? `Resend OTP in ${countdown}s` : "Didn't receive? Resend OTP"}
                </button>
              </div>
            </div>

            <button
              className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </>
        )}

        {/* Password Input - Step 3 */}
        {currentStep === 'password' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-white/40 mb-1.5 tracking-wide uppercase">New Password</label>
              <div className="relative w-full">
                <input
                  className={`w-full px-4 py-2.5 pl-10 pr-10 border rounded-xl text-white text-sm bg-[#1e2d4a] placeholder-white/20 focus:outline-none focus:ring-2 transition-all ${
                    errors.password ? 'border-red-500/60 focus:ring-red-500/30' : 'border-white/10 focus:ring-[#F05A22]/40 focus:border-[#F05A22]/50'
                  }`}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={formValues.password}
                  onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30">
                  <FiLock size={18} />
                </span>
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-400 text-xs mt-1.5 px-1">{errors.password}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/40 mb-1.5 tracking-wide uppercase">Confirm Password</label>
              <div className="relative w-full">
                <input
                  className={`w-full px-4 py-2.5 pl-10 pr-10 border rounded-xl text-white text-sm bg-[#1e2d4a] placeholder-white/20 focus:outline-none focus:ring-2 transition-all ${
                    errors.confirmPassword ? 'border-red-500/60 focus:ring-red-500/30' : 'border-white/10 focus:ring-[#F05A22]/40 focus:border-[#F05A22]/50'
                  }`}
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={formValues.confirmPassword}
                  onChange={(e) => setFormValues({ ...formValues, confirmPassword: e.target.value })}
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30">
                  <FiLock size={18} />
                </span>
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-red-400 text-xs mt-1.5 px-1">{errors.confirmPassword}</p>}
            </div>

            <button
              className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
              onClick={handleResetPassword}
              disabled={loading}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </>
        )}

        {/* API Error Display */}
        {errors.api && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
            {errors.api}
          </div>
        )}
      </div>
    )}
  </div>
</div>
  );
};

export default ForgotPasswordPage;